<template>
  <div :class="cardClasses">
    <DeferredContent v-if="hasMedia" :class="mediaClasses">
      <img
        v-if="item?.img && !item?.zoom_img && usesIntrinsicLeftImage"
        :src="`/media/${item.img}.webp`"
        :alt="mediaAlt"
        :class="intrinsicImageClasses"
        :style="intrinsicImageStyle"
      />
      <div
        v-else-if="item?.img && !item?.zoom_img"
        :class="imageClasses"
        :style="imageStyle"
        role="img"
        :aria-label="mediaAlt"
      ></div>
      <Image
        v-else-if="item?.img"
        :alt="mediaAlt"
        preview
        :class="usesIntrinsicLeftImage ? '' : 'w-full h-full'"
      >
        <template #image>
          <img
            v-if="usesIntrinsicLeftImage"
            :src="`/media/${item.img}.webp`"
            :alt="mediaAlt"
            :class="intrinsicImageClasses"
            :style="intrinsicImageStyle"
          />
          <div
            v-else
            :class="imageClasses"
            :style="imageStyle"
            role="img"
            :aria-label="mediaAlt"
          ></div>
        </template>
        <template #preview="slotProps">
          <img
            :class="slotProps.class"
            :src="`/media/${item.img}.webp`"
            :alt="mediaAlt"
            :style="slotProps.style"
            @click="slotProps.onClick"
          />
        </template>
      </Image>

      <div
        v-else-if="item?.youtube_id"
        :class="imageClasses"
        :style="youtubeImageStyle"
        role="img"
        :aria-label="displayName ? `${displayName} video thumbnail` : 'Video thumbnail'"
      ></div>

      <div v-else-if="item?.icon" :class="iconClasses">
        <i :class="`pi ${item.icon} text-7xl text-color`" aria-hidden="true" />
      </div>
    </DeferredContent>

    <div :class="contentClasses">
      <div
        v-if="item?.info_columns?.length"
        class="text-sm text-800 flex flex-wrap align-items-center gap-2"
      >
        <div
          v-for="column in item.info_columns"
          :key="column.field"
          class="flex gap-2 align-items-center"
        >
          <PersonAvatarGroup
            v-if="column.layout === 'person' && column.relatedItems?.length"
            :people="column.relatedItems"
          />
          <i
            v-else-if="column.layout !== 'person'"
            class="pi pi-circle-fill green"
            style="font-size: 0.5rem"
          />
          <div class="font-light text-xs text-color">
            {{ column.value }}
          </div>
        </div>
      </div>
      <div
        v-if="item.show_title !== false && displayName"
        :class="['generic-card__title', getTextClass(web_content_type), 'white-space-normal']"
      >
        {{ displayName }}
      </div>
      <ScrollPanel v-if="web_content_type.scrollable_body && bodyHtml" class="h-5rem">
        <div class="generic-card__body text-sm" v-html="bodyHtml"></div>
      </ScrollPanel>
      <div v-else-if="bodyHtml" class="generic-card__body text-sm" v-html="bodyHtml"></div>

      <div v-if="item?.web_links?.length" class="generic-card__actions">
        <Links :web_links="item.web_links" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { getPersonDisplayName, getTextClass } from '~/composables/useUtils'

const props = defineProps(['item', 'web_content_type'])

const displayName = computed(() => {
  if (!props.item?.person_id) {
    return props.item?.name
  }

  return getPersonDisplayName(props.item?.person_title, props.item?.name)
})

const hasMedia = computed(() => {
  return (
    props.item?.path !== 'resources/courses' &&
    props.web_content_type?.show_img &&
    Boolean(props.item?.img || props.item?.youtube_id || props.item?.icon)
  )
})

const isLeftMedia = computed(() => {
  return hasMedia.value && props.web_content_type?.img_pos === 'left'
})

const usesIntrinsicLeftImage = computed(() => {
  const imageFit = props.web_content_type?.img_width ?? 'contain'

  return isLeftMedia.value && Boolean(props.item?.img) && imageFit === 'contain'
})

const hasFixedWidth = computed(() => {
  return /^w-\d+rem$/.test(props.web_content_type?.width ?? '')
})

const bodyHtml = computed(() => {
  if (props.web_content_type?.body_summary) {
    return props.item?.summary ?? props.item?.body
  }
  return props.item?.body
})

const mediaAlt = computed(() => {
  return displayName.value ? `${displayName.value} image` : 'Card image'
})

const mediaHeightClass = computed(() => `h-${props.web_content_type?.img_height ?? '15rem'}`)

const imageClasses = computed(() => {
  const imageFit = props.web_content_type?.img_width ?? 'contain'
  const breakPoint = props.web_content_type?.break_pt
  const usesResponsiveTopCrop =
    props.web_content_type?.img_pos === 'top' &&
    props.web_content_type?.width === 'col-4' &&
    imageFit === 'cover' &&
    Boolean(breakPoint)
  const imagePositionClasses = usesResponsiveTopCrop
    ? ['bg-top', `${breakPoint}:bg-center`]
    : ['bg-center']

  return [
    'generic-card__image',
    'bg-no-repeat',
    `bg-${imageFit}`,
    ...imagePositionClasses,
    mediaHeightClass.value,
    'w-full',
  ]
})

const intrinsicImageClasses = computed(() => {
  return ['generic-card__intrinsic-image', 'block', 'w-auto', 'max-w-full', mediaHeightClass.value]
})

const iconClasses = computed(() => {
  return [
    'generic-card__image',
    'flex',
    'w-full',
    'justify-content-center',
    'align-items-center',
    mediaHeightClass.value,
  ]
})

const imageStyle = computed(() => {
  return {
    backgroundImage: `url('/media/${props.item?.img}.webp')`,
    backgroundColor: props.web_content_type?.img_bg_color || undefined,
  }
})

const intrinsicImageStyle = computed(() => {
  return {
    backgroundColor: props.web_content_type?.img_bg_color || undefined,
  }
})

const youtubeImageStyle = computed(() => {
  return {
    backgroundImage: `url('https://img.youtube.com/vi/${props.item?.youtube_id}/hqdefault.jpg')`,
  }
})

const cardClasses = computed(() => {
  const backgroundColor = props.web_content_type?.bg_color ?? 'white'
  const height = props.web_content_type?.height

  return [
    'generic-card',
    'min-w-0',
    'border-round',
    'p-2',
    `bg-${backgroundColor}`,
    height ? `h-${height}` : '',
    hasFixedWidth.value ? 'max-w-full' : '',
    'flex',
    'flex-column',
    isLeftMedia.value ? 'sm:flex-row sm:align-items-start' : '',
  ].filter(Boolean)
})

const mediaClasses = computed(() => {
  if (usesIntrinsicLeftImage.value) {
    return [
      'generic-card__media',
      'generic-card__media--intrinsic-image',
      'flex-shrink-0',
      mediaHeightClass.value,
    ]
  }

  if (isLeftMedia.value) {
    return [
      'generic-card__media',
      'w-full',
      'sm:w-20rem',
      'max-w-full',
      'flex-shrink-0',
      mediaHeightClass.value,
    ]
  }

  return ['generic-card__media', 'w-full', mediaHeightClass.value]
})

const contentClasses = computed(() => {
  return [
    'generic-card__content',
    'w-full',
    'min-w-0',
    'my-3',
    'px-3',
    'sm:px-4',
    'flex',
    'flex-column',
    'gap-2',
    isLeftMedia.value ? 'flex-1' : '',
  ].filter(Boolean)
})
</script>

<style lang="scss" scoped>
.generic-card__media--intrinsic-image {
  width: fit-content;
  max-width: 100%;
  margin-right: auto;
  margin-left: auto;
}

.generic-card__intrinsic-image {
  object-fit: contain;
}

.generic-card__content {
  overflow-wrap: anywhere;
}

.generic-card__body :deep(> p:first-child) {
  margin-top: 0;
}

@media screen and (min-width: 576px) {
  .generic-card__media--intrinsic-image {
    margin-right: 0;
    margin-left: 0;
  }
}
</style>
